import { useEffect, useRef } from "react";
import { get, size } from "lodash-es";
import { Carousel, Skeleton } from "antd";
import { Category as CategoryComp } from "@/components";
import { useCurrent } from "@/providers";
import { Personal } from "../personal";
import styles from "./style.module.scss";
import { useMemo, useState } from "react";
import { useCurrentPage } from "@/hooks/use-current-page";
import { fetchNoticeList } from "@/services/notice";
import { Icon } from "@terminus/dqh-web-ui";
import dayjs from "dayjs";
import { EllipsisText } from "@terminus/dqh-web-ui";

export type CategoryPropsType = {
  layout?: "horzontal" | "vertical";
};

export const Category = () => {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [imageLoaded, setImageLoaded] = useState<Record<string, boolean>>({});
  const [allAnnouncementBid, setAllAnnouncementBid] = useState<any[]>([]);
  const [currentDisplayIndex, setCurrentDisplayIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const isTestEnv = process.env.YCX_HOME === "https://ycx-test.terminus.io";

  const fetchAllData = () => {
    fetchNoticeList({
      page: 1,
      pageSize: 15,
    }).then((res) => {
      setAllAnnouncementBid(res.list || []);
    });
  };
  const startLocalCarousel = () => {
    if (allAnnouncementBid.length <= 3) return;

    intervalRef.current = setInterval(() => {
      setCurrentDisplayIndex((prev) => {
        const nextIndex = prev + 3;
        return nextIndex >= allAnnouncementBid.length ? 0 : nextIndex;
      });
    }, 5000);
  };
  const stopCarousel = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const getCurrentDisplayData = () => {
    if (allAnnouncementBid.length === 0) return [];

    const endIndex = Math.min(
      currentDisplayIndex + 3,
      allAnnouncementBid.length
    );
    return allAnnouncementBid.slice(currentDisplayIndex, endIndex);
  };

  const { currentPage } = useCurrentPage();
  const [layout, setLayout] = useState<"horzontal" | "vertical">("horzontal");
  useEffect(() => {
    if (!currentPage) return;
    setLayout(
      currentPage === "business-opportunity" ? "vertical" : "horzontal"
    );
  }, [currentPage]);
  useEffect(() => {
    if (currentPage === "business-opportunity") {
      stopCarousel();
      setCurrentDisplayIndex(0);
      fetchAllData();
    }
  }, [currentPage]);

  useEffect(() => {
    if (
      currentPage === "business-opportunity" &&
      allAnnouncementBid.length > 0
    ) {
      startLocalCarousel();
    }

    return () => {
      stopCarousel();
    };
  }, [allAnnouncementBid, currentPage]);

  const {
    layoutImages: { data = [] },
  } = useCurrent();
  const currentBanners = useMemo(() => {
    const mainBanner = data.find(
      (item: { resourceName: string; imgFile: string; directUrl: string }) =>
        item.resourceName === "主banner"
    );

    const list = get(mainBanner, "relateSettings", [])
      .map((item: { imgFile: string; directUrl: string }) => {
        return { imgFile: item.imgFile, directUrl: item.directUrl };
      })
      .filter((item: any) => !imageErrors[item.imgFile]);

    if (!size(list)) return [];

    return list;
  }, [data, imageErrors]);

  useEffect(() => {
    if (currentBanners.length > 0) {
      setImageLoaded({});
      currentBanners.forEach((item: { imgFile?: string; directUrl?: string }) => {
        if (item.imgFile) {
          const img = new Image();
          img.onload = () => {
            setImageLoaded((prev) => ({
              ...prev,
              [item.imgFile!]: true,
            }));
          };
          img.onerror = () => {
            setImageErrors((prev) => ({
              ...prev,
              [item.imgFile!]: true,
            }));
          };
          img.src = item.imgFile;
          if (img.complete && img.naturalHeight !== 0) {
            setImageLoaded((prev) => ({
              ...prev,
              [item.imgFile!]: true,
            }));
          }
        }
      });
    }
  }, [currentBanners.map((b: { imgFile?: string; directUrl?: string }) => b.imgFile).join(',')]);

  const currentSubRightTopBanner = useMemo(() => {
    const mainBanner = data.find(
      (item: { resourceName: string; imgFile: string }) =>
        item.resourceName === "右上banner"
    );

    const list = get(mainBanner, "relateSettings", []).map(
      (item: { imgFile: string; directUrl: string }) => {
        return {
          imgFile: item.imgFile,
          directUrl: item.directUrl,
        };
      }
    );

    if (!size(list)) return [];

    return list;
  }, [data]);

  const currentSubLeftBottomBanner = useMemo(() => {
    const mainBanner = data.find(
      (item: { resourceName: string; imgFile: string }) =>
        item.resourceName === "左下banner"
    );

    const list = get(mainBanner, "relateSettings", []).map(
      (item: { imgFile: string; directUrl: string }) => {
        return {
          imgFile: item.imgFile,
          directUrl: item.directUrl,
        };
      }
    );

    if (!size(list)) return [];

    return list;
  }, [data]);

  const currentSubRightBottomBanner = useMemo(() => {
    const mainBanner = data.find(
      (item: { resourceName: string; imgFile: string }) =>
        item.resourceName === "右下banner"
    );

    const list = get(mainBanner, "relateSettings", []).map(
      (item: { imgFile: string; directUrl: string }) => {
        return {
          imgFile: item.imgFile,
          directUrl: item.directUrl,
        };
      }
    );

    if (!size(list)) return [];

    return list;
  }, [data]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className={styles.categoryWrapper}>
        <>
          {/* 原有 Category 页面内容不变 */}
          <div className={styles.categoryLeft} style={{ padding: 8 }}>
            <CategoryComp
                title={
                  currentPage === "business-opportunity"
                    ? "商机分类"
                    : "供应商分类"
                }
            />
          </div>
          <div
            className={styles.categoryMid}
            style={{
              flexDirection: layout === "vertical" ? "column" : "row",
            }}
          >
            {(() => {
              const allImagesLoaded = currentBanners.length > 0 && 
                currentBanners.every((item: { imgFile?: string; directUrl?: string }) => imageLoaded[item.imgFile!] || imageErrors[item.imgFile!]);
              
              if (!currentBanners.length) {
                return (
                  <Skeleton.Input active style={{
                    height: layout === "vertical" ? 190 : 306,
                    width: layout === "vertical" ? 744 : 516,
                  }} />
                );
              }
              
              return (
                <div
                  style={{
                    height: layout === "vertical" ? 190 : 306,
                    width: layout === "vertical" ? "100%" : 516,
                    position: "relative",
                  }}
                  className={styles.categoryMidTop}
                >
                  {!allImagesLoaded && (
                    <Skeleton.Input active style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: layout === "vertical" ? 190 : 306,
                      width: layout === "vertical" ? "100%" : 516,
                      zIndex: 1,
                    }} />
                  )}
                  <div style={{ opacity: allImagesLoaded ? 1 : 0, transition: "opacity 0.3s" }}>
                    <Carousel autoplay>
                      {currentBanners.map(
                        (item: { imgFile?: string; directUrl?: string }) => (
                          <img
                            key={item.imgFile}
                            src={item.imgFile!}
                            className={
                              layout === "vertical"
                                ? styles.categoryMidTopItem
                                : styles.categoryMidTopMoreItem
                            }
                            style={{ cursor: "pointer" }}
                            onLoad={(e) => {
                              const img = e.currentTarget;
                              if (img.complete && img.naturalHeight !== 0) {
                                setImageLoaded((prev) => ({
                                  ...prev,
                                  [item.imgFile!]: true,
                                }));
                              }
                            }}
                            onError={() => {
                              setImageErrors((prev) => ({
                                ...prev,
                                [item.imgFile!]: true,
                              }));
                            }}
                            onClick={() => {
                              if (item.directUrl) {
                                window.open(item.directUrl, "_blank");
                              }
                            }}
                          />
                        )
                      )}
                    </Carousel>
                  </div>
                </div>
              );
            })()}

            {layout === "horzontal" && (
              <div className={styles.categoryMidRight}>
                {currentSubRightTopBanner?.[0]?.imgFile &&
                  !imageErrors[currentSubRightTopBanner[0].imgFile] && (
                    <>
                      {
                        currentBanners?.length ?<img
                        src={currentSubRightTopBanner[0].imgFile}
                        className={styles.categoryMidRightImg}
                        style={{
                          marginBottom: 16,
                          cursor: "pointer",
                        }}
                        onError={() => {
                          setImageErrors((prev) => ({
                            ...prev,
                            [currentSubRightTopBanner[0].imgFile]: true,
                          }));
                        }}
                        onClick={() => {
                          if (currentSubRightTopBanner[0].directUrl) {
                            window.open(
                              currentSubRightTopBanner[0].directUrl,
                              "_blank"
                            );
                          }
                        }}
                      />:
                        <Skeleton.Input active style={{
                          width:212,
                          height:145,
                          marginBottom: 16,
                          cursor: "pointer",
                        }} />
                      }
                      </>
                    )}
                {currentSubRightBottomBanner?.[0]?.imgFile &&
                  !imageErrors[currentSubRightBottomBanner[0].imgFile] && (
                    <>
                      {
                        currentBanners?.length ? <img
                        src={currentSubRightBottomBanner?.[0]?.imgFile}
                        className={styles.categoryMidRightImg}
                        style={{
                          cursor: "pointer",
                        }}
                        onError={() => {
                          setImageErrors((prev) => ({
                            ...prev,
                            [currentSubRightBottomBanner[0].imgFile]: true,
                          }));
                        }}
                        onClick={() => {
                          if (currentSubRightBottomBanner?.[0]?.directUrl) {
                            window.open(
                              currentSubRightBottomBanner?.[0]?.directUrl,
                              "_blank"
                            );
                          }
                        }}
                      />:<Skeleton.Input active style={{
                        width:212,
                        height:145,
                        marginBottom: 16,
                        cursor: "pointer",
                      }} />
                      }
                    </>
                  )}
              </div>
            )}

            {layout === "vertical" && (
              <div className={styles.categoryMidBot}>
                {
                  currentBanners?.length ? <img
                  style={{
                    marginRight: 16,
                    cursor: "pointer",
                    maxWidth: "calc(50% - 16px)",
                  }}
                  src={
                    currentPage === "business-opportunity"
                      ? currentSubLeftBottomBanner?.[0]?.imgFile
                      : currentSubRightTopBanner?.[0]?.imgFile
                  }
                  className={styles.categoryMidBotImg}
                  onClick={() => {
                    if (currentSubRightTopBanner?.[0]?.directUrl) {
                      window.open(
                        currentSubRightTopBanner?.[0]?.directUrl,
                        "_blank"
                      );
                    }
                    if (currentSubLeftBottomBanner?.[0]?.directUrl) {
                      window.open(
                        currentSubLeftBottomBanner?.[0]?.directUrl,
                        "_blank"
                      );
                    }
                  }}
                />:<Skeleton.Input active  style={{
                    width: 356,
                    height:100,
                    marginRight: 16,
                    cursor: "pointer",
                  }} />
                }
                {
                  currentBanners?.length ? <img
                  src={currentSubRightBottomBanner?.[0]?.imgFile}
                  className={styles.categoryMidBotImg}
                  style={{ cursor: "pointer", maxWidth: "50%" }}
                  onClick={() => {
                    if (currentSubRightBottomBanner?.[0]?.directUrl) {
                      window.open(
                        currentSubRightBottomBanner?.[0]?.directUrl,
                        "_blank"
                      );
                    }
                  }}
                />:<Skeleton.Input active style={{
                  width: 356,
                  height:100,
                  cursor: "pointer",
                }} />
                }
                
              </div>
            )}
          </div>
          {
            currentBanners?.length ? <Personal /> : <Skeleton.Input active style={{width:212,height:306}} />
          }
          
        </>
      </div>
      {currentPage === "business-opportunity" && !isTestEnv && (
        <div className={styles.announcementBidWrapper}>
          <div className={styles.announcementBid}>
            <img
              src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/daqihui-pc/louderImg.png"
              alt="公告图标"
              className={styles.louderImg}
            />
            <img
              src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/daqihui-pc/announceBidTitle.svg"
              alt="公告标题"
              className={styles.announceBidTitle}
            />
            <div className={styles.announcementBidList}>
              {getCurrentDisplayData()?.map((item) => (
                <div
                  key={item?.noticeId}
                  className={styles.announcementBidItem}
                >
                  <EllipsisText
                    text={`${item?.noticeSubject} ${dayjs(
                      item?.createDate
                    ).format("YYYY-MM-DD")}`}
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              className={styles.moreButton}
              style={{ position: "relative", zIndex: 100 }}
              onClick={() => {
                window.open("/notice", "_blank");
              }}
            >
              查看更多 <Icon name="youright" color="#fff" size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
